import type { CatchUp } from "@beacon/core/catch-up"
import type {
  InboxDelivery,
  InboxPage,
  InboxUnread,
  WorkNoticeDelivery,
} from "@beacon/core/beacon-inbox"
import { infiniteQueryOptions, queryOptions, type QueryClient } from "@tanstack/react-query"
import { call } from "@/administration/client.ts"
import { chatKeys } from "@/chat/client.ts"

/*
 * The Member's Beacon inbox in the web app: their entries, how many are unread, and where their
 * work notices alert them. Every read is the web API's, through the Member's access as it is then;
 * another device reading or choosing says so (an `inbox` hint), and everything here is read again.
 */

/** Under Chat's own keys, so forgetting Chat forgets the inbox too. */
export const inboxKeys = {
  all: [...chatKeys.all, "inbox"] as const,
  entries: [...chatKeys.all, "inbox", "entries"] as const,
  unread: [...chatKeys.all, "inbox", "unread"] as const,
  delivery: [...chatKeys.all, "inbox", "delivery"] as const,
  catchUp: [...chatKeys.all, "inbox", "catch-up"] as const,
}

/** The Member's entries, latest first, a page at a time. */
export const inboxQuery = infiniteQueryOptions({
  queryKey: inboxKeys.entries,
  initialPageParam: undefined as string | undefined,
  queryFn: ({ pageParam }) =>
    call<InboxPage>(
      "GET",
      `/chat/inbox${pageParam === undefined ? "" : `?${new URLSearchParams({ cursor: pageParam }).toString()}`}`,
    ),
  getNextPageParam: (last) => last.nextCursor ?? undefined,
})

/** How many entries are unread, for the badges; read again each minute as unreads are. */
export const inboxUnreadQuery = queryOptions({
  queryKey: inboxKeys.unread,
  queryFn: () => call<InboxUnread>("GET", "/chat/inbox/unread"),
  refetchInterval: 60_000,
})

/** Reads the entries `entryIds` names, or every one when none are named, on every device. */
export const markInboxRead = (entryIds?: readonly string[]) =>
  call<InboxUnread>("PUT", "/chat/inbox/read", entryIds === undefined ? {} : { entryIds })

/** Where the Member's work notices alert them. */
export const inboxDeliveryQuery = queryOptions({
  queryKey: inboxKeys.delivery,
  queryFn: () => call<InboxDelivery>("GET", "/chat/inbox/delivery"),
  staleTime: 60_000,
})

export const setInboxDelivery = (workNotices: WorkNoticeDelivery) =>
  call<InboxDelivery>("PUT", "/chat/inbox/delivery", { workNotices })

/**
 * The Member's catch-up as they may read it now. While Beacon writes it, it is read again every few
 * seconds, in case the hint that it is written never comes.
 */
export const catchUpQuery = queryOptions({
  queryKey: inboxKeys.catchUp,
  queryFn: () => call<CatchUp>("GET", "/chat/inbox/catch-up"),
  refetchInterval: (query) =>
    query.state.data?.on && query.state.data.state === "writing" ? 5_000 : false,
})

/** Turns the Member's catch-up on (asking for one) or off (forgetting it). */
export const setCatchUp = (on: boolean) => call<CatchUp>("PUT", "/chat/inbox/catch-up", { on })

/** Asks for the catch-up to cover what is unread now; `again`, the Member asking themselves. */
export const askCatchUp = (again = false) =>
  call<CatchUp>("POST", "/chat/inbox/catch-up", again ? { again } : {})

/** Reads the whole inbox again: entries, the unread count and the choice. */
export const refreshInbox = (queryClient: QueryClient) =>
  queryClient.invalidateQueries({ queryKey: inboxKeys.all })
